import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  MessageCircle,
  Users,
} from 'lucide-react';
import {
  buildWhatsAppHref,
  countOnlineOrders,
  formatDate,
  getClienteDisplayName,
  getClienteInitials,
  getHealthClass,
  priorityClass,
} from '../utils/crmDashboardHelpers';
import {
  ActionChip,
  CustomerAvatar,
  SortHeader,
  renderCountOrDash,
  renderMoneyOrDash,
} from './CrmDashboardUi';

export default function CrmClientesList({
  loading,
  errorMessage,
  sort,
  onSort,
  paginatedClientes,
  visibleClientes,
  search,
  onOpenCliente,
  pageStart,
  pageEnd,
  totalPages,
  safePage,
  pageNumbers,
  onPageChange,
}) {
  if (loading) {
    return <div className="crm-loading">Carregando clientes…</div>;
  }

  if (errorMessage) {
    return <div className="crm-alert">{errorMessage}</div>;
  }

  return (
    <>
      <div className="crm-table-head">
        <div><SortHeader label="Cliente" sortKey="name" sort={sort} onSort={onSort} /></div>
        <div><SortHeader label="Pedidos online" sortKey="online" sort={sort} onSort={onSort} /></div>
        <div><SortHeader label="Visitas no salão" sortKey="visits" sort={sort} onSort={onSort} /></div>
        <div><SortHeader label="Total gasto" sortKey="total" sort={sort} onSort={onSort} /></div>
        <div><SortHeader label="Última ação" sortKey="action" sort={sort} onSort={onSort} /></div>
        <div><SortHeader label="Quando" sortKey="date" sort={sort} onSort={onSort} /></div>
        <div></div>
      </div>
      <div className="crm-list">
        {paginatedClientes.map((item) => {
          const itemWhatsAppHref = buildWhatsAppHref(item.telefone || item.phone);
          const itemDisplayName = getClienteDisplayName(item);
          const itemInitials = getClienteInitials(item);
          const health = getHealthClass(item);
          const onlineCount = countOnlineOrders(item);
          const salonCount = (item.visitas || []).length;

          return (
            <button
              type="button"
              className={`crm-row ${priorityClass(item)}`}
              key={item.id || item.phone}
              onClick={() => onOpenCliente(item)}
            >
              <div className="crm-cell left">
                <CustomerAvatar
                  photo={item.photo}
                  initials={itemInitials}
                  health={health}
                />
                <div>
                  <div className="crm-name">{itemDisplayName}</div>
                  <div className="crm-phone">{item.phone || 'Sem telefone'}</div>
                </div>
              </div>
              <div>{renderCountOrDash(onlineCount)}</div>
              <div>{renderCountOrDash(salonCount)}</div>
              <div>{renderMoneyOrDash(item.totalSpent)}</div>
              <div>
                <ActionChip action={item.lastAction} />
              </div>
              <div>
                {item.lastAction?.date ? (
                  formatDate(item.lastAction.date)
                ) : (
                  <span className="crm-num muted">-</span>
                )}
              </div>
              <div className="crm-row-actions">
                {itemWhatsAppHref && (
                  <a
                    className="crm-btn small inline-icon whatsapp"
                    href={itemWhatsAppHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Enviar mensagem via WhatsApp para ${itemDisplayName}`}
                    title="Enviar mensagem via WhatsApp"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <MessageCircle size={14} />
                  </a>
                )}
                <span className="crm-btn small inline-icon" title="Abrir perfil"><Eye size={14} /></span>
              </div>
            </button>
          );
        })}
        {!visibleClientes.length && (
          <div className="crm-empty">
            <div className="crm-empty-icon">
              <Users size={32} />
            </div>
            <div className="crm-empty-title">
              {search.trim()
                ? `Sem resultados para "${search.trim()}"`
                : 'Nenhum cliente nesta seleção'}
            </div>
            <div className="crm-empty-sub">
              {search.trim()
                ? 'Tente buscar por outro nome ou parte do telefone.'
                : 'Ajuste os filtros para ver mais clientes.'}
            </div>
          </div>
        )}
      </div>
      {!!visibleClientes.length && (
        <div className="crm-results-info">
          <span>
            Mostrando{' '}
            <strong>
              {pageStart + 1}–{Math.min(pageEnd, visibleClientes.length)}
            </strong>{' '}
            de <strong>{visibleClientes.length}</strong>{' '}
            {visibleClientes.length === 1 ? 'cliente' : 'clientes'}
          </span>
          {totalPages > 1 && (
            <nav className="crm-pagination" aria-label="Paginação">
              <button
                type="button"
                onClick={() => onPageChange((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                aria-label="Página anterior"
              >
                <ChevronLeft size={14} />
              </button>
              {pageNumbers.map((p, idx) =>
                p === '…' ? (
                  <span key={`ellipsis-${idx}`} className="crm-pagination-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onPageChange(p)}
                    className={p === safePage ? 'active' : ''}
                    aria-current={p === safePage ? 'page' : undefined}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => onPageChange((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                aria-label="Próxima página"
              >
                <ChevronRight size={14} />
              </button>
            </nav>
          )}
        </div>
      )}
    </>
  );
}
